/*
 * From https://www.redblobgames.com/x/2104-screen-space-arrows/
 *
 * Much of this code is from Ricky Reusser, MIT licensed
 * https://observablehq.com/@rreusser/debugging-normals-without-duplicating-geometry-data#comment-d24c59e3c1d3b842
 */

const canvas = document.querySelector("canvas#main");

const bunny = require('bunny');
const normals = require('angle-normals')(bunny.cells, bunny.positions);

const createREGL = require('regl');
const regl = createREGL({
    canvas: canvas,
    extensions: ['ANGLE_instanced_arrays'],
});

const mesh = {
    a_vertex: regl.buffer(bunny.positions),
    elements: regl.elements(bunny.cells),
    a_normal: regl.buffer(normals),
    faceCount: bunny.cells.length,
    vertexCount: bunny.positions.length,
};

let drawMesh = regl({
    vert: `
        precision highp float;
        attribute vec3 a_vertex, a_normal;
        uniform mat4 projection, view;
        varying vec3 v_normal;

        void main() {
            v_normal = a_normal;
            gl_Position = projection * view * vec4(a_vertex, 1);
        }
    `,
    frag: `
        precision highp float;
        varying vec3 v_normal;

        void main() {
            gl_FragColor = vec4(0.5 + 0.5 * v_normal, 1);
        }
    `,
    attributes: {
        a_vertex: regl.prop('a_vertex'),
        a_normal: regl.prop('a_normal'),
    },
    elements: regl.prop('elements'),
});

let drawNormals = regl({
    vert: `
        precision highp float;
        uniform mat4 projection, view;
        uniform float u_tailwidth, u_aspect, u_scale, u_foreshortening;
        uniform vec2 u_arrowhead_shape;
        attribute vec3 a_vertex, a_normal;
        attribute vec4 a_arrow;
        varying vec3 v_normal;

        void main() {
            v_normal = a_normal;

            vec4 vp = view * vec4(a_vertex, 1);
            vec4 vpn = view * vec4(a_vertex + u_scale * a_normal, 1);

            // project arrow start into homogeneous coordinates
            vec4 p = projection * vp;

            // project arrow end into homogeneous coordinates
            vec4 pn = projection * vpn;

            float foreshortening = max(0.1, sqrt(1.0 - u_foreshortening * abs((vp - vpn).z) / length((vp - vpn).xyz)));

            // a_arrow.y tells us how far along the arrow (tail to head)
            gl_Position = mix(p, pn, a_arrow.y);

            // compute a screen-space vector parallel to the arrow
            vec2 unit_vector = normalize((pn.xy / pn.w - p.xy / p.w) * vec2(u_aspect, 1));
            vec2 perp_unit_vector = vec2(-unit_vector.y, unit_vector.x);

            // perturb the point according to the arrow data
            gl_Position.xy += (
                // offset to make the arrow wider
                perp_unit_vector * (a_arrow.x * u_tailwidth + a_arrow.w * u_arrowhead_shape.y)
                // and along the arrow length
                + unit_vector * a_arrow.z * u_arrowhead_shape.x * foreshortening

                // ricky says final step is "a bit tricky"
            ) / vec2(u_aspect, 1) * gl_Position.w;
        }
    `,
    frag: `
        precision highp float;
        uniform vec4 u_color;
        varying vec3 v_normal;

        void main() {
            gl_FragColor = vec4(vec3(1, 1, 1) - v_normal, 1);
        }
    `,
    attributes: {
        a_vertex: {
            buffer: regl.prop('a_vertex'),
            divisor: 1,
            stride: 12,
        },
        a_normal: {
            buffer: regl.prop('a_normal'),
            divisor: 1,
            stride: 12,
        },
        a_arrow: new Float32Array([
            // The per-instance triangles are defined in terms of four pieces of data
            // that tell us where on the arrow we are, using the mesh vertex and mesh
            // normal as inputs:
            //    y: direction of the arrow (tail = 0, head = 1)
            //    x: width of the arrow
            //    z: arrowhead length-wise offset in screen space
            //    w: arrowhead width-wise offset in screen space

            // Tail is made up of two triangles to make a quad:
            -1, 0, 0, 0,
            1, 0, 0, 0,
            1, 1, -1, 0,
            -1, 0, 0, 0,
            1, 1, -1, 0,
            -1, 1, -1, 0,

            // Head is made up of one triangle:
            0, 1, -1, -1,
            0, 1, -1, 1,
            0, 1, 0, 0,
        ]),
    },
    uniforms: {
        u_tailwidth: (ctx, props) => (props.arrowtail_width / ctx.framebufferHeight) * ctx.pixelRatio,
        u_aspect: ctx => ctx.framebufferWidth / ctx.framebufferHeight,
        u_scale: regl.prop('arrow_scale'),
        u_foreshortening: (_ctx, props) => props.foreshortening? 1 : 0,
        u_arrowhead_shape: (ctx, props) => [
            (props.arrowhead_length / ctx.framebufferHeight) * ctx.pixelRatio * 2.0,
            (props.arrowhead_width / ctx.framebufferHeight) * ctx.pixelRatio,
        ],
        u_color: regl.prop('arrow_color'),
    },
    primitive: 'triangles',
    instances: (_ctx, props) => props.vertexCount, // one instance per vertex
    count: 9, // nine instances per vertex
});

const camera = require('regl-camera')(regl, {distance: 17, phi: 0.2, center: [0, 4.5, 0], noScroll: true,});

regl.frame(() => {
    camera((state) => {
        if (!state.dirty) return;
        regl.clear({color: [0, 0, 0, 1]});
        drawMesh(mesh);
        drawNormals({arrowtail_width: 1,
                     arrow_scale: 0.8,
                     foreshortening: true,
                     arrowhead_length: 5,
                     arrowhead_width: 3,
                     arrow_color: [0, 0.3, 0.5, 1],
                     ...mesh});
    });
});
